<template>
    <div
        class="text-right w-100 d-flex flex-column"
        style="gap: var(--space-3)"
    >
        <div
            class="d-flex flex-row justify-content-between align-items-center"
            style="gap: var(--space-4)"
        >
            <!-- Image preview -->
            <slot name="image-preview"></slot>

            <div class="d-flex flex-column w-100" style="gap: var(--space-1)">
                <label
                    class="upload-container"
                    :class="{
                        'upload-container-enabled': !disabled,
                        'upload-container-disabled': disabled,
                    }"
                    :for="inputId"
                    v-on:mouseenter="hovering = true"
                    v-on:mouseleave="hovering = false"
                    v-on:dragenter="handleDragEvent"
                    v-on:dragover="handleDragEvent"
                    v-on:dragend="handleDragEvent"
                    v-on:dragexit="handleDragEvent"
                    v-on:dragleave="handleDragEvent"
                    v-on:drop="onFileChange"
                    v-on:change="onFileChange"
                >
                    <div v-if="uploading" class="logo-loader">
                        <img
                            src="@/assets/images/logos/ipac-loader/logo-loader.svg"
                            style="width: 3.625rem; height: 3.625rem"
                            class="logo-svg"
                            alt="IPAC Loader"
                        />
                    </div>
                    <i
                        v-else
                        class="fi fi-upload-cloud"
                        :class="{ 'fi-hovered': hovering }"
                    ></i>
                    <br />

                    <template v-if="!uploading">
                        <span class="upper-text">Select a file to upload</span
                        ><br />
                        <span class="text-muted lower-text"
                            >or drag and drop it here</span
                        >
                    </template>
                    <span v-else class="upper-text">Uploading...</span>

                    <input
                        type="file"
                        :id="inputId"
                        class="d-none"
                        :multiple="multiple"
                        :accept="acceptedTypesInput"
                        :disabled="disabled"
                    />
                </label>

                <div
                    v-if="error"
                    class="text-danger d-flex justify-content-end"
                >
                    {{ error }}
                </div>
            </div>
        </div>

        <div
            class="details text-muted font-italic d-flex justify-content-end flex-wrap"
        >
            <div v-if="acceptedTypesText" class="text-end">
                {{ acceptedTypesText }} types accepted.
            </div>
            &nbsp;
            <div v-if="maxImageHeight || maxImageWidth">
                (max. {{ maxImageWidth }}px x {{ maxImageHeight }}px)
            </div>
        </div>
    </div>
</template>

<script>
import { useAdminStore } from '@/stores/admin';

export default {
    name: 'UploadField',
    props: {
        accept: {
            // 'image', '*' (any), or list of specific types (ex. ["jpg", "jpeg", "png"])
            type: [String, Array],
            default: '*',
        },
        maxImageWidth: {
            // max width (if accept == 'image')
            type: Number,
            default: null,
        },
        maxImageHeight: {
            // max height (if accept == 'image')
            type: Number,
            default: null,
        },
        multiple: {
            // allow uploading multiple files at a time
            type: Boolean,
            default: false,
        },
        preventUpload: {
            // Prevent upload when "Upload file" is clicked. Instead return file blob and emit "upload"
            type: Boolean,
            default: false,
        },
        disabled: {
            // input is disabled?
            type: Boolean,
            default: false,
        },
    },
    components: {},

    data() {
        return {
            dragging: false,
            files: null,
            error: '',
            inputId: '',
            hovering: false,

            uploading: false,
            numUploaded: 0,
            numToUpload: 0,

            adminStore: null,
        };
    },
    emits: ['upload', 'uploaded', 'progress', 'uploading'], // 'upload' is when "Upload File" is clicked (ie before actual upload) if preventUpload is true.  'uploaded' is after upload is completed

    watch: {
        uploading(isUploading) {
            this.$emit('uploading', isUploading);
        },
        numUploaded() {
            if (this.numUploaded == this.numToUpload) {
                this.uploading = false;
                this.files = null;
            }
        },
    },
    computed: {
        acceptedTypesText() {
            if (this.acceptedTypes == '*') return '';

            const types = this.acceptedTypes;
            const prefix = types.slice(0, types.length - 1).join(', ');
            const lastType = types[types.length - 1];

            const result = prefix ? [prefix, lastType].join(' & ') : lastType;
            return result.toUpperCase();
        },
        acceptedTypesInput() {
            if (typeof this.acceptedTypes == 'string') return this.accept;
            return this.acceptedTypes
                .map((fileType) => '.' + fileType)
                .join(',');
        },

        acceptedTypes() {
            if (this.accept == 'image') return ['jpg', 'jpeg', 'gif', 'png'];
            return this.accept;
        },
        fileText() {
            if (this.files && this.files.length > 0) {
                if (this.files.length > 1 || window.innerWidth < 767.98)
                    // multiple files or mobile
                    return `(${this.files.length}) file${
                        this.files.length > 1 ? 's' : ''
                    } selected.`;

                let MAX_LENGTH = 30;
                let text = this.files[0].name;
                let tokens = text.split('.');

                if (tokens.length < 2)
                    // invalid file  (not in format "name.ext")
                    return '';

                const ext = tokens[tokens.length - 1];
                const nameLength = text.length - ext.length;

                if (nameLength > MAX_LENGTH)
                    text =
                        text.slice(0, MAX_LENGTH) +
                        '... ' +
                        '.' +
                        tokens[tokens.length - 1];

                return text;
            }
            return '';
        },
    },
    methods: {
        handleDragEvent(e) {
            e.preventDefault();
            e.stopPropagation();

            if (['dragenter', 'dragover'].includes(e.type))
                this.dragging = true;
            else if (['dragleave', 'dragend', 'dragexit'].includes(e.type))
                this.dragging = false;
        },

        onFileChange(e) {
            e.preventDefault();
            e.stopPropagation();
            this.dragging = false;

            if (this.disabled || this.uploading) return;

            this.files = null;
            this.uploading = false;

            const files = e.target.files || e.dataTransfer.files;
            this.error = '';

            for (let i = 0; i < files.length; i++) {
                const tokens = files[i].name.split('.');
                if (tokens.length < 2) this.error = 'Unknown file type.';

                const fileType = tokens[tokens.length - 1].toLowerCase();
                if (
                    this.acceptedTypes != '*' &&
                    !this.acceptedTypes.includes(fileType)
                )
                    this.error = `Invalid file type (${files[i].name})`;
            }

            if (!this.error && files.length) {
                this.files = files;
                this.uploadFile();
            }
        },

        getImageDimensions(imageFile) {
            // Return width and height of image file
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const img = new Image();
                        img.onload = () => {
                            resolve({ width: img.width, height: img.height });
                        };
                        img.src = reader.result;
                    } catch (error) {
                        reject(error.toString());
                    }
                };
                reader.readAsDataURL(imageFile);
            });
        },

        uploadFile() {
            this.uploading = true;
            this.numUploaded = 0;
            this.numToUpload = this.files.length;

            Array.from(this.files).forEach((file) => {
                // Validate width and height if image
                const isFileImage = file.type.startsWith('image');

                // If image is a file
                if (
                    isFileImage &&
                    (this.maxImageHeight || this.maxImageWidth)
                ) {
                    this.getImageDimensions(file).then((dims) => {
                        if (
                            this.maxImageHeight &&
                            dims.height > this.maxImageHeight
                        ) {
                            this.error = `Image height must be under ${this.maxImageHeight}px!`;
                            this.files = null;
                            this.uploading = false;
                            return;
                        } else if (
                            this.maxImageWidth &&
                            dims.width > this.maxImageWidth
                        ) {
                            this.error = `Image width must be under ${this.maxImageWidth}px!`;
                            this.files = null;
                            this.uploading = false;
                            return;
                        }

                        this.performSingleUpload(file);
                    });
                }

                // Otherwise
                else {
                    this.performSingleUpload(file);
                }
            });

            if (this.preventUpload) this.uploading = false;

            document.getElementById(this.inputId).value = '';
            this.files = [];
        },

        performSingleUpload(file) {
            const vm = this;
            this.$emit('upload', file);

            if (this.preventUpload) return;

            const formData = new FormData();
            formData.append('file', file);

            const overrideCompanyId = this.adminStore.companyId;
            if (overrideCompanyId !== null && overrideCompanyId !== undefined) {
                formData.append('company_id', overrideCompanyId);
            }
            
            const isAdmin = this.$route.path.startsWith('/admin');
            if (isAdmin) {
                formData.append('app_admin_only', true);
            }

            const xhr = new XMLHttpRequest();

            const isFileImage = file.type.startsWith('image');
            const endpoint = isFileImage ? 'upload/image' : 'upload/file';
            const url = this.$env.apiRoot + endpoint;
            xhr.open('POST', url);
            xhr.withCredentials = true; // This sends cookies with the request

            xhr.onloadstart = (e) => {
                console.log('started', e);
            };

            xhr.onloadend = (e) => {
                try {
                    const response = JSON.parse(e.target.response);

                    if (!response.success) {
                        vm.error = response.message;
                        vm.files = null;
                        vm.uploading = false;
                        vm.numUploaded = 0;
                        vm.numToUpload = 0;
                        return;
                    } else {
                        vm.numUploaded += 1;
                        vm.$emit('uploaded', response, file);
                    }
                } catch (e) {
                    console.error(e);
                }
            };

            xhr.upload.onprogress = (e) => {
                vm.$emit('progress', e);
            };

            xhr.send(formData);
        },
    },
    created() {
        this.inputId = Math.floor(Math.random() * 10000).toString();
        this.adminStore = useAdminStore();
    },
};
</script>

<style scoped>
.upload-container {
    width: 100%;
    border: 1px dashed var(--neutral-700);
    border-radius: 16px;
    padding: var(--space-3, 0.75rem) 2.6875rem;
    text-align: center;
    margin: 0;
    font-size: 14px;
}

.upload-container-disabled {
    background-color: var(--neutral-500);
    cursor: not-allowed;
}

.upload-container-enabled {
    background-color: var(--neutral-400);
}

.upload-container-enabled:hover {
    cursor: pointer;
    background-color: var(--neutral-500);
    transition: 400ms ease;
}

.upper-text {
    font-family: Montserrat, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
}

.lower-text {
    font-weight: 400;
}

.details {
    font-size: 0.875rem;
}

label > i {
    color: var(--neutral-700);
    font-size: 58px;
    font-weight: 100;
    -webkit-text-stroke: 2px var(--neutral-400);
}

.fi-hovered {
    -webkit-text-stroke: 2px var(--neutral-500) !important;
    transition: 400ms ease;
}

.logo-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--neutral-700);
}

.logo-svg {
    animation: logoLoader 4s linear infinite;
    color: var(--neutral-700);
}

@keyframes logoLoader {
    0% {
        transform: scale(1) rotate(0deg);
    }
    50% {
        transform: scale(0.7) rotate(-180deg);
    }
    100% {
        transform: scale(1) rotate(-360deg);
    }
}

@keyframes logoShrink {
    0% {
        transform: scale(1) rotate(0deg);
    }
    100% {
        transform: scale(0.7) rotate(-180deg);
    }
}
</style>
